import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddBusinessRoundedIcon from '@mui/icons-material/AddBusinessRounded';
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import { normalizeHall, getHallCardStatus } from '../utils/normalizeHall';
import { buildHallFormData } from '../utils/hallPayload';
import { updateHall, deleteHall as deleteHallApi } from '../api';
import HallEditDrawer from './HallEditDrawer';
import { HALL_FORM_ACCENT } from '../utils/hallDrawerStyles';

const CONFIRM_WORD = 'confirm';

const addHallPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 225, 182, 0.35); }
  70% { transform: scale(1.04); box-shadow: 0 0 0 14px rgba(76, 225, 182, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(76, 225, 182, 0); }
`;

const addHallShimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

function normalizeHallRow(hall) {
  return normalizeHall(hall);
}

function HallCardSkeleton({ isDark }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        overflow: 'hidden',
        minHeight: 360,
        borderRadius: '22px',
        border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.06)}`,
        bgcolor: isDark ? alpha('#fff', 0.03) : '#fff',
      }}
    >
      <Skeleton variant="rectangular" height={168} animation="wave" />
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.25 }}>
          <Skeleton variant="rounded" height={58} sx={{ flex: 1, borderRadius: '14px' }} />
          <Skeleton variant="rounded" height={58} sx={{ flex: 1, borderRadius: '14px' }} />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Skeleton variant="rounded" height={58} sx={{ flex: 1, borderRadius: '14px' }} />
          <Skeleton variant="rounded" height={58} sx={{ flex: 1, borderRadius: '14px' }} />
        </Stack>
        <Stack direction="row" spacing={0.75}>
          <Skeleton variant="rounded" height={52} sx={{ flex: 1, borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={52} sx={{ flex: 1, borderRadius: '12px' }} />
          <Skeleton variant="rounded" height={52} sx={{ flex: 1, borderRadius: '12px' }} />
        </Stack>
      </Box>
    </Paper>
  );
}

function isHallArchived(hall) {
  return hall?.is_active === false || hall?.status === 'inactive';
}

export default function HallManagementWorkspace({
  halls,
  loading = false,
  refreshing = false,
  isDark,
  tokens,
  surfaceSx,
  formatCurrency,
  onAddHall,
  onShowMessage,
  onRefresh,
}) {
  const navigate = useNavigate();
  const [hallRows, setHallRows] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedHall, setSelectedHall] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, hall: null });
  const [confirmInput, setConfirmInput] = useState('');

  useEffect(() => {
    // Keep previous cards visible during quiet refresh; only sync when payload arrives.
    if (loading && !(halls || []).length) return;
    setHallRows((halls || []).map(normalizeHallRow));
  }, [halls, loading]);

  const filteredRows = useMemo(() => [...hallRows], [hallRows]);

  const openEditDrawer = (hall) => {
    setSelectedHall(hall);
    setDrawerOpen(true);
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, action: null, hall: null });
    setConfirmInput('');
  };

  const openInactiveConfirm = (hall) => {
    if (!hall?.id || String(hall.id).startsWith('local-')) return;
    if (isHallArchived(hall)) {
      // Activate does not require typed confirmation.
      applyArchiveToggle(hall);
      return;
    }
    setConfirmInput('');
    setConfirmDialog({ open: true, action: 'inactive', hall });
  };

  const openDeleteConfirm = (hall) => {
    if (!hall?.id) return;
    if (String(hall.id).startsWith('local-')) {
      setHallRows((prev) => prev.filter((row) => String(row.id) !== String(hall.id)));
      if (String(selectedHall?.id) === String(hall.id)) setDrawerOpen(false);
      return;
    }
    setConfirmInput('');
    setConfirmDialog({ open: true, action: 'delete', hall });
  };

  const applyArchiveToggle = async (hall) => {
    if (!hall?.id || String(hall.id).startsWith('local-')) return;
    const archived = isHallArchived(hall);
    const updatedHall = {
      ...hall,
      status: archived ? 'available' : 'inactive',
      is_active: archived,
    };
    setArchivingId(hall.id);
    try {
      const formData = buildHallFormData(updatedHall);
      await updateHall(hall.id, formData);
      onShowMessage?.(
        'success',
        archived ? 'Hall activated successfully' : 'Hall marked inactive successfully',
      );
      await onRefresh?.();
    } catch (err) {
      onShowMessage?.('error', err?.userMessage || err?.message || 'Failed to update hall status');
    } finally {
      setArchivingId(null);
    }
  };

  const applySoftDelete = async (hall) => {
    if (!hall?.id) return;
    setDeletingId(hall.id);
    try {
      await deleteHallApi(hall.id);
      if (String(selectedHall?.id) === String(hall.id)) {
        setDrawerOpen(false);
        setSelectedHall(null);
      }
      onShowMessage?.(
        'success',
        'Hall deleted successfully. Existing bookings remain linked to this hall.',
      );
      await onRefresh?.();
    } catch (err) {
      onShowMessage?.('error', err?.userMessage || err?.message || 'Failed to delete hall');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDialogValid = confirmInput.trim().toLowerCase() === CONFIRM_WORD;
  const confirmBusy = Boolean(
    confirmDialog.hall
    && (
      (confirmDialog.action === 'delete' && deletingId === confirmDialog.hall.id)
      || (confirmDialog.action === 'inactive' && archivingId === confirmDialog.hall.id)
    ),
  );

  const handleConfirmAction = async () => {
    const { action, hall } = confirmDialog;
    if (!hall || !action) return;

    if (action === 'delete' && !confirmDialogValid) return;

    if (action === 'delete') {
      await applySoftDelete(hall);
    } else if (action === 'inactive') {
      await applyArchiveToggle(hall);
    }
    closeConfirmDialog();
  };

  const statusChipSx = (status) => {
    const key = String(status || '').toLowerCase();
    const color = key === 'available'
      ? '#4ce1b6'
      : key === 'occupied' || key === 'maintenance' || key === 'tentative'
        ? '#fbbf24'
        : key === 'inactive'
          ? '#94a3b8'
          : '#8ac9ff';
    return {
      color,
      bgcolor: alpha(color, isDark ? 0.2 : 0.14),
      border: `1px solid ${alpha(color, 0.35)}`,
      fontWeight: 800,
      textTransform: 'capitalize',
    };
  };

  const hallActionBtnSx = (color) => ({
    flex: 1,
    minWidth: 0,
    py: 0.85,
    px: 0.5,
    border: 'none',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.3,
    bgcolor: alpha(color, isDark ? 0.12 : 0.1),
    color,
    fontSize: '0.66rem',
    fontWeight: 850,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    lineHeight: 1.1,
    cursor: 'pointer',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
    boxShadow: `inset 0 1px 0 ${alpha('#fff', isDark ? 0.06 : 0.55)}`,
    '&:hover:not(:disabled)': {
      transform: 'translateY(-1px)',
      bgcolor: alpha(color, isDark ? 0.2 : 0.16),
      boxShadow: `0 8px 16px ${alpha(color, 0.22)}`,
    },
    '&:disabled': { opacity: 0.45, cursor: 'not-allowed' },
  });

  const metricTileSx = (color) => ({
    flex: 1,
    minWidth: 0,
    p: 1,
    borderRadius: '14px',
    background: isDark
      ? `linear-gradient(160deg, ${alpha('#fff', 0.05)} 0%, ${alpha(color, 0.1)} 100%)`
      : `linear-gradient(160deg, #ffffff 0%, ${alpha(color, 0.1)} 100%)`,
    border: `1px solid ${alpha(color, isDark ? 0.28 : 0.2)}`,
    boxShadow: isDark ? 'none' : `0 6px 14px ${alpha(color, 0.08)}`,
  });

  const closeEditDrawer = () => {
    setDrawerOpen(false);
    setSelectedHall(null);
  };

  const gridSx = {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(4, minmax(0, 1fr))',
    },
    gap: 2,
    width: '100%',
    opacity: refreshing ? 0.82 : 1,
    transition: 'opacity 0.25s ease',
  };

  const renderAddHallCard = () => {
    const isFirstHall = filteredRows.length === 0;
    return (
      <Paper
        component="button"
        type="button"
        elevation={0}
        onClick={onAddHall}
        sx={{
          p: 0,
          minHeight: 360,
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          overflow: 'hidden',
          borderRadius: '22px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          color: 'inherit',
          font: 'inherit',
          background: isDark
            ? `linear-gradient(155deg, ${alpha('#0f172a', 0.95)} 0%, ${alpha('#11332c', 0.95)} 48%, ${alpha('#0b1f1a', 0.98)} 100%)`
            : `linear-gradient(155deg, #f7fffc 0%, #e8fff6 42%, #f4fbf8 100%)`,
          boxShadow: isDark
            ? `0 18px 40px ${alpha('#000', 0.35)}, inset 0 1px 0 ${alpha('#fff', 0.06)}`
            : `0 16px 34px ${alpha('#4ce1b6', 0.16)}, 0 2px 0 ${alpha('#fff', 0.8)} inset`,
          transition: 'transform 0.22s ease, box-shadow 0.22s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '22px',
            padding: '1.5px',
            background: `linear-gradient(135deg, ${alpha('#4ce1b6', 0.95)}, ${alpha('#8ac9ff', 0.55)}, ${alpha('#4ce1b6', 0.35)})`,
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          },
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: isDark
              ? `0 24px 48px ${alpha('#000', 0.45)}, 0 0 0 1px ${alpha('#4ce1b6', 0.25)}`
              : `0 22px 44px ${alpha('#4ce1b6', 0.28)}`,
          },
          '&:hover .add-hall-orb': {
            animation: `${addHallPulse} 1.6s ease-out infinite`,
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: isDark ? 0.22 : 0.35,
            backgroundImage: `
              radial-gradient(circle at 18% 22%, ${alpha('#4ce1b6', 0.35)} 0%, transparent 42%),
              radial-gradient(circle at 84% 78%, ${alpha('#8ac9ff', 0.28)} 0%, transparent 40%),
              repeating-linear-gradient(135deg, ${alpha('#4ce1b6', 0.08)} 0 2px, transparent 2px 14px)
            `,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -30,
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${alpha('#4ce1b6', 0.35)}, ${alpha('#8ac9ff', 0.12)})`,
            filter: 'blur(6px)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2.25,
            py: 3,
            gap: 1.75,
          }}
        >
          <Box
            className="add-hall-orb"
            sx={{
              width: 78,
              height: 78,
              borderRadius: '24px',
              display: 'grid',
              placeItems: 'center',
              background: `linear-gradient(145deg, #4ce1b6 0%, #2bbf96 55%, #1fa87f 100%)`,
              backgroundSize: '200% 200%',
              animation: `${addHallShimmer} 4.5s ease infinite`,
              color: '#031512',
              boxShadow: `0 14px 28px ${alpha('#4ce1b6', 0.35)}`,
            }}
          >
            <AddBusinessRoundedIcon sx={{ fontSize: 36 }} />
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                color: tokens.text,
                fontWeight: 950,
                fontSize: '1.05rem',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {isFirstHall ? 'Add your first hall' : 'Add another hall'}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                color: tokens.subtleText,
                fontSize: '0.82rem',
                lineHeight: 1.45,
                maxWidth: 220,
                mx: 'auto',
              }}
            >
              {isFirstHall
                ? 'Set up pricing, capacity, and availability to start taking bookings.'
                : 'Expand your portfolio with a new venue space.'}
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 0.5,
              px: 1.5,
              py: 0.7,
              borderRadius: '999px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              bgcolor: alpha('#4ce1b6', isDark ? 0.16 : 0.18),
              color: isDark ? '#4ce1b6' : '#0f766e',
              fontWeight: 900,
              fontSize: '0.72rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
            New Hall
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Stack spacing={2.5}>
      <Paper sx={surfaceSx}>
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" sx={{ color: tokens.text, fontWeight: 900 }}>
              Hall Management
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.subtleText }}>
              Manage halls, configurations, pricing, and availability.
            </Typography>
          </Box>
          {(loading || refreshing) ? (
            <Chip
              size="small"
              icon={<CircularProgress size={12} color="inherit" />}
              label={loading ? 'Loading halls' : 'Updating'}
              sx={{
                fontWeight: 800,
                bgcolor: alpha('#4ce1b6', isDark ? 0.16 : 0.12),
                color: isDark ? '#4ce1b6' : '#0f766e',
                '& .MuiChip-icon': { color: 'inherit', ml: 0.75 },
              }}
            />
          ) : null}
        </Box>

        {loading ? (
          <Box sx={gridSx}>
            {[0, 1].map((key) => (
              <HallCardSkeleton key={`hall-skeleton-${key}`} isDark={isDark} />
            ))}
          </Box>
        ) : (
        <Box sx={gridSx}>
          {filteredRows.map((hall) => {
            const archived = isHallArchived(hall);
            const cardStatus = getHallCardStatus(hall);
            const statusStyles = statusChipSx(cardStatus);
            const businessLabel = hall.business_type_label || hall.hall_type || 'Hall';
            return (
              <Paper
                key={hall.id}
                elevation={0}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/hall/detail/${hall.id}/`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    navigate(`/hall/detail/${hall.id}/`);
                  }
                }}
                sx={{
                  p: 0,
                  overflow: 'hidden',
                  minHeight: 360,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '22px',
                  position: 'relative',
                  cursor: 'pointer',
                  opacity: archived ? 0.8 : 1,
                  border: `1px solid ${isDark ? alpha('#fff', 0.1) : alpha('#0f172a', 0.06)}`,
                  background: isDark
                    ? `linear-gradient(165deg, ${alpha('#111827', 0.98)} 0%, ${alpha('#0f172a', 0.96)} 55%, ${alpha(HALL_FORM_ACCENT, 0.1)} 100%)`
                    : `linear-gradient(165deg, #ffffff 0%, #f8fafc 52%, ${alpha(HALL_FORM_ACCENT, 0.12)} 100%)`,
                  boxShadow: isDark
                    ? `0 14px 34px ${alpha('#000', 0.35)}`
                    : `0 16px 36px ${alpha('#0f172a', 0.08)}, 0 1px 0 ${alpha('#fff', 0.8)} inset`,
                  transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease, border-color 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: alpha(HALL_FORM_ACCENT, 0.45),
                    boxShadow: isDark
                      ? `0 22px 44px ${alpha('#000', 0.42)}, 0 0 0 1px ${alpha(HALL_FORM_ACCENT, 0.2)}`
                      : `0 24px 48px ${alpha(HALL_FORM_ACCENT, 0.18)}, 0 8px 20px ${alpha('#0f172a', 0.08)}`,
                  },
                }}
              >
                <Box sx={{ position: 'relative', height: 158, flexShrink: 0, overflow: 'hidden' }}>
                  {hall.thumbnail ? (
                    <Box
                      component="img"
                      src={hall.thumbnail}
                      alt={hall.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.35s ease',
                        '.MuiPaper-root:hover &': { transform: 'scale(1.04)' },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'grid',
                        placeItems: 'center',
                        background: `linear-gradient(135deg, ${alpha(HALL_FORM_ACCENT, 0.22)} 0%, ${alpha('#0f172a', isDark ? 0.5 : 0.12)} 100%)`,
                        color: tokens.subtleText,
                      }}
                    >
                      <MapsHomeWorkIcon sx={{ fontSize: 44, opacity: 0.5, color: HALL_FORM_ACCENT }} />
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: `
                        linear-gradient(180deg, ${alpha('#000', 0.18)} 0%, ${alpha('#000', 0.2)} 35%, ${alpha('#020617', 0.88)} 100%),
                        radial-gradient(80% 60% at 100% 0%, ${alpha(HALL_FORM_ACCENT, 0.22)} 0%, transparent 55%)
                      `,
                      pointerEvents: 'none',
                    }}
                  />
                  <Chip
                    size="small"
                    label={cardStatus}
                    sx={{
                      ...statusStyles,
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      height: 26,
                      backdropFilter: 'blur(8px)',
                      boxShadow: `0 6px 14px ${alpha('#000', 0.2)}`,
                    }}
                  />
                  <Box sx={{ position: 'absolute', left: 14, right: 14, bottom: 12 }}>
                    <Typography
                      component="div"
                      sx={{
                        color: '#ffffff !important',
                        WebkitTextFillColor: '#ffffff',
                        fontWeight: 950,
                        fontSize: '1.12rem',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2,
                        textShadow: `0 1px 2px ${alpha('#000', 0.9)}, 0 2px 12px ${alpha('#000', 0.65)}`,
                      }}
                      noWrap
                    >
                      {hall.name}
                    </Typography>
                    <Stack direction="row" spacing={0.75} sx={{ mt: 0.7 }} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={hall.hall_code || '—'}
                        sx={{
                          height: 22,
                          fontSize: '0.66rem',
                          fontWeight: 800,
                          color: '#031512',
                          bgcolor: alpha('#fff', 0.95),
                        }}
                      />
                      <Chip
                        size="small"
                        label={businessLabel}
                        sx={{
                          height: 22,
                          fontSize: '0.66rem',
                          fontWeight: 800,
                          color: '#ffffff',
                          bgcolor: alpha(HALL_FORM_ACCENT, 0.45),
                          border: `1px solid ${alpha('#fff', 0.3)}`,
                          maxWidth: 140,
                        }}
                      />
                    </Stack>
                  </Box>
                </Box>

                <Box sx={{ p: 1.6, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.35 }}>
                  <Stack direction="row" spacing={0.85}>
                    <Box sx={metricTileSx(HALL_FORM_ACCENT)}>
                      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.35 }}>
                        <GroupsOutlinedIcon sx={{ fontSize: 15, color: HALL_FORM_ACCENT }} />
                        <Typography sx={{ color: tokens.subtleText, fontSize: '0.6rem', fontWeight: 850, letterSpacing: '0.07em' }}>
                          CAPACITY
                        </Typography>
                      </Stack>
                      <Typography sx={{ color: tokens.text, fontWeight: 950, fontSize: '0.95rem', lineHeight: 1.1 }}>
                        {hall.capacity || 0}
                      </Typography>
                    </Box>
                    <Box sx={metricTileSx('#38bdf8')}>
                      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.35 }}>
                        <EventAvailableOutlinedIcon sx={{ fontSize: 15, color: '#38bdf8' }} />
                        <Typography sx={{ color: tokens.subtleText, fontSize: '0.6rem', fontWeight: 850, letterSpacing: '0.07em' }}>
                          TODAY
                        </Typography>
                      </Stack>
                      <Typography sx={{ color: tokens.text, fontWeight: 950, fontSize: '0.95rem', lineHeight: 1.1 }}>
                        {hall.today_bookings || 0}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      p: 1.1,
                      borderRadius: '14px',
                      border: `1px solid ${alpha(HALL_FORM_ACCENT, 0.22)}`,
                      background: isDark
                        ? `linear-gradient(135deg, ${alpha(HALL_FORM_ACCENT, 0.12)} 0%, ${alpha('#fff', 0.03)} 100%)`
                        : `linear-gradient(135deg, ${alpha(HALL_FORM_ACCENT, 0.12)} 0%, ${alpha('#fff', 0.9)} 100%)`,
                    }}
                  >
                    <Stack direction="row" spacing={1} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: alpha(tokens.text, 0.1) }} />}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PaidOutlinedIcon sx={{ fontSize: 14, color: HALL_FORM_ACCENT }} />
                          <Typography sx={{ color: tokens.subtleText, fontSize: '0.6rem', fontWeight: 850, letterSpacing: '0.06em' }}>
                            HOURLY
                          </Typography>
                        </Stack>
                        <Typography sx={{ color: tokens.text, fontWeight: 900, fontSize: '0.82rem', mt: 0.25 }} noWrap>
                          {formatCurrency(hall.pricing?.hourly_rate || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <WbSunnyOutlinedIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                          <Typography sx={{ color: tokens.subtleText, fontSize: '0.6rem', fontWeight: 850, letterSpacing: '0.06em' }}>
                            FULL DAY
                          </Typography>
                        </Stack>
                        <Typography sx={{ color: tokens.text, fontWeight: 900, fontSize: '0.82rem', mt: 0.25 }} noWrap>
                          {formatCurrency(hall.pricing?.full_day_rate || 0)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 'auto',
                    px: 1.35,
                    pb: 1.35,
                    pt: 0.25,
                    display: 'flex',
                    gap: 0.75,
                  }}
                >
                  <Tooltip title="Edit hall">
                    <Box
                      component="button"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        openEditDrawer(hall);
                      }}
                      sx={hallActionBtnSx(HALL_FORM_ACCENT)}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 18 }} />
                      Edit
                    </Box>
                  </Tooltip>
                  <Tooltip title={archived ? 'Activate hall' : 'Mark hall inactive'}>
                    <Box
                      component="button"
                      type="button"
                      disabled={archivingId === hall.id || deletingId === hall.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        openInactiveConfirm(hall);
                      }}
                      sx={hallActionBtnSx('#f59e0b')}
                    >
                      {archived ? <UnarchiveIcon sx={{ fontSize: 18 }} /> : <ArchiveIcon sx={{ fontSize: 18 }} />}
                      {archivingId === hall.id ? 'Saving' : (archived ? 'Activate' : 'Inactive')}
                    </Box>
                  </Tooltip>
                  <Tooltip title={archived ? 'Hall is already inactive' : 'Delete hall'}>
                    <Box
                      component="button"
                      type="button"
                      disabled={archived || deletingId === hall.id || archivingId === hall.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        openDeleteConfirm(hall);
                      }}
                      sx={{
                        ...hallActionBtnSx('#fb7185'),
                        ...(archived ? { opacity: 0.45, cursor: 'not-allowed' } : null),
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      Delete
                    </Box>
                  </Tooltip>
                </Box>
              </Paper>
            );
          })}
          {renderAddHallCard()}
        </Box>
        )}
      </Paper>

      <HallEditDrawer
        open={drawerOpen}
        hall={selectedHall}
        isDark={isDark}
        onClose={closeEditDrawer}
        onSaved={async () => {
          await onRefresh?.();
        }}
        onNotice={onShowMessage}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={confirmBusy ? undefined : closeConfirmDialog}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? '#0f172a' : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ color: isDark ? '#fff' : '#212121', fontWeight: 700, pb: 1.5 }}>
          {confirmDialog.action === 'delete' ? 'Delete Hall' : 'Mark Hall Inactive'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: isDark ? '#ddd' : '#666', fontSize: '1rem' }}>
            {confirmDialog.action === 'delete' ? (
              <>
                Soft-delete hall <strong>"{confirmDialog.hall?.name}"</strong>? Existing bookings and
                events stay linked. Type <strong>confirm</strong> below to proceed.
              </>
            ) : (
              <>
                Mark hall <strong>"{confirmDialog.hall?.name}"</strong> as inactive? It will not
                accept new bookings until activated again.
              </>
            )}
          </DialogContentText>
          {confirmDialog.action === 'delete' ? (
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label='Type "confirm"'
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              disabled={confirmBusy}
              error={confirmInput.length > 0 && !confirmDialogValid}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDark ? alpha('#ffffff', 0.04) : '#ffffff',
                },
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2.5,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
          }}
        >
          <Button onClick={closeConfirmDialog} disabled={confirmBusy} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmDialog.action === 'delete' ? 'error' : 'warning'}
            disabled={
              confirmBusy
              || (confirmDialog.action === 'delete' && !confirmDialogValid)
            }
            startIcon={confirmBusy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {confirmBusy
              ? 'Working...'
              : (confirmDialog.action === 'delete' ? 'Delete Hall' : 'Mark Inactive')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
